import { z } from "zod";

export const classificationTypes = [
  "Organization",
  "Person",
  "Customer",
  "Product",
  "Commercial",
  "Process",
  "Policy",
  "Metric",
  "Strategy",
  "Risk",
  "Agent",
  "Event",
  "Knowledge",
] as const;

export const ClassificationTypeSchema = z.enum(classificationTypes);

export type ClassificationType = z.infer<typeof ClassificationTypeSchema>;

export const ClassificationSourceTypeSchema = z.enum(["fact"]);

export type ClassificationSourceType = z.infer<
  typeof ClassificationSourceTypeSchema
>;

export const ClassificationSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  sourceType: ClassificationSourceTypeSchema,
  sourceId: z.uuid(),
  sourceVersionId: z.uuid().nullable(),
  type: ClassificationTypeSchema,
  secondaryTypes: z.array(ClassificationTypeSchema),
  subject: z.string(),
  classificationVersion: z.string(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
});

export type Classification = z.infer<typeof ClassificationSchema>;

export const ClassificationFieldsSchema = ClassificationSchema.pick({
  type: true,
  secondaryTypes: true,
  subject: true,
});

export type ClassificationFields = z.infer<typeof ClassificationFieldsSchema>;

/**
 * The fields a fact, block or MCP response carries, empty until classified
 */
export const ClassificationSummarySchema = z.object({
  type: ClassificationTypeSchema.nullable(),
  secondaryTypes: z.array(ClassificationTypeSchema),
  subject: z.string().nullable(),
});

export type ClassificationSummary = z.infer<typeof ClassificationSummarySchema>;

export function toClassificationSummary(
  classification: Classification | null | undefined,
): ClassificationSummary {
  if (!classification) {
    return { type: null, secondaryTypes: [], subject: null };
  }
  return {
    type: classification.type,
    secondaryTypes: classification.secondaryTypes,
    subject: classification.subject,
  };
}

/**
 * Fact filters shared by MCP list_facts and the assistant's recallFacts
 */
export const ClassificationFilterSchema = z.object({
  types: z
    .array(ClassificationTypeSchema)
    .optional()
    .describe(
      "Only facts whose primary type is one of these, e.g. Policy for internal rules or Customer for facts about a customer.",
    ),
  subject: z
    .string()
    .trim()
    .min(1)
    .optional()
    .describe(
      "Only facts about this subject, matched in any case, e.g. a customer, product or policy name as returned in a fact's subject.",
    ),
});
